import { type Component, createMemo, For, Show, Suspense, untrack } from "solid-js";
import { twJoin } from "tailwind-merge";
import InfoIcon from "~/Icons/18_Icon_Info.svg?component-solid";
import { ModalV2Ui, Spinner } from "~/solidJs/shared";
import { CheckBox } from "~/solidJs/shared/ui/atoms/CheckBox";
import { useUpdateSeatsContext } from "../methods/updateSeats";

// Utility functions outside component to avoid recreation on each render
const formatDate = (dateStr: string): string => {
	const date = new Date(dateStr);
	return date.toLocaleDateString("en-US", {
		month: "long",
		day: "numeric",
		year: "numeric"
	});
};

const calculateDaysUntil = (dateStr: string): number => {
	const targetDate = new Date(dateStr);
	const today = new Date();
	const diffTime = targetDate.getTime() - today.getTime();
	return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const MemberListInfo: Component = () => {
	const { selectedCount, totalSeats } = useUpdateSeatsContext();

	return (
		<div class="typography-text-14-medium flex items-center gap-1.5 text-text-secondary">
			<span class={twJoin(selectedCount() > 0 ? "text-status-accent-error" : "")}>
				{selectedCount()} selected
			</span>
			<span>•</span>
			<span>{totalSeats()} seats</span>
		</div>
	);
};

const MemberRow: Component<{
	companyAccountId: number;
	userName: string;
	isCurrentUser: boolean;
}> = (props) => {
	const { handleCheckboxChange, selectedSeats, canRemoveSeat } = useUpdateSeatsContext();

	// Memoize selection state to avoid recalculation on each access
	const isSelected = createMemo(() => selectedSeats().has(props.companyAccountId));
	const isDisabled = createMemo(
		() => props.isCurrentUser || !canRemoveSeat(props.companyAccountId)
	);

	const handleClick = () => {
		if (!isDisabled()) {
			handleCheckboxChange(props.companyAccountId);
		}
	};

	return (
		<button
			class={twJoin(
				"flex h-14 shrink-0 items-center gap-2 rounded-2xl border border-solid px-3 py-3 transition-colors",
				isDisabled()
					? "border-border-secondary pl-11"
					: isSelected()
						? "border-status-accent-error bg-status-error-background pl-4"
						: "border-border-secondary pl-4 hover:border-border-gray-dark"
			)}
			disabled={isDisabled()}
			onClick={handleClick}
			type="button"
		>
			<Show when={!isDisabled()}>
				<CheckBox.ArkClosedCheckBox
					checked={isSelected()}
					onCheckedChange={handleClick}
					onClick={(e) => e.stopPropagation()}
					disabled={isDisabled()}
				>
					<div class="caption-13-medium flex flex-1 items-center gap-1 text-text-secondary-v2">
						<span>{props.userName}</span>
					</div>
				</CheckBox.ArkClosedCheckBox>
			</Show>
			<Show when={isDisabled()}>
				<div class="caption-13-medium flex flex-1 items-center gap-1 text-text-secondary-v2">
					<span>{props.userName}</span>
					<Show when={props.isCurrentUser}>
						<span class="text-text-tertiary">(you)</span>
					</Show>
				</div>
			</Show>
		</button>
	);
};

const UnusedSeatRow: Component<{ companyAccountId: number }> = (props) => {
	const { handleCheckboxChange, selectedSeats, canRemoveSeat } = useUpdateSeatsContext();

	// Memoize selection state to avoid recalculation on each access
	const isSelected = createMemo(() => selectedSeats().has(props.companyAccountId));
	const isDisabled = createMemo(() => !canRemoveSeat(props.companyAccountId));

	const handleClick = () => {
		if (!isDisabled()) {
			handleCheckboxChange(props.companyAccountId);
		}
	};

	return (
		<button
			class={twJoin(
				"flex h-14 shrink-0 items-center gap-2 rounded-2xl border border-dashed px-3 py-3 pl-4 transition-colors",
				isSelected()
					? "border-status-accent-error bg-status-error-background"
					: "border-border-tertiary hover:border-border-gray-dark"
			)}
			onClick={handleClick}
			type="button"
			disabled={isDisabled()}
		>
			<CheckBox.ArkClosedCheckBox
				checked={isSelected()}
				onCheckedChange={handleClick}
				onClick={(e) => e.stopPropagation()}
				disabled={isDisabled()}
			>
				<div class="caption-13-medium flex flex-1 items-center text-text-secondary-v2">
					<span>Unused seat</span>
				</div>
			</CheckBox.ArkClosedCheckBox>
		</button>
	);
};

const MembersList: Component = () => {
	const { teamQuery, teamMembers, isOwner } = useUpdateSeatsContext();

	return (
		<div class="flex max-h-[308px] w-full flex-col gap-2 overflow-y-auto">
			<Show when={teamQuery.data} fallback={<Spinner />}>
				<For each={teamMembers()}>
					{(member) => (
						<Show
							when={member.user_id !== null}
							fallback={
								<UnusedSeatRow companyAccountId={member.company_account_id} />
							}
						>
							<MemberRow
								companyAccountId={member.company_account_id}
								userName={member.user_name || "Unknown"}
								isCurrentUser={isOwner(member)}
							/>
						</Show>
					)}
				</For>
			</Show>
		</div>
	);
};

const InfoBanner: Component = () => {
	const { selectedCount, teamQuery } = useUpdateSeatsContext();

	// Memoize next payment date to avoid recalculation
	const nextPaymentDate = createMemo(() => {
		const teamData = teamQuery.data;
		return teamData?.next_payment?.payment_date ?? null;
	});

	return (
		<div class="flex items-center gap-2 rounded-xl border border-border-secondary bg-white p-4">
			<InfoIcon class="shrink-0 text-text-secondary" />
			<Suspense
				fallback={
					<p class="typography-text-14-medium font-normal text-text-secondary-v2">
						Cutting seats increases seat price. More seats mean lower cost.
					</p>
				}
			>
				<Show
					when={selectedCount() > 0}
					fallback={
						<p class="typography-text-14-medium font-normal text-text-secondary-v2">
							Cutting seats increases seat price. More seats mean lower cost.
						</p>
					}
				>
					<div class="typography-text-14-medium flex flex-col gap-0 font-normal text-text-secondary-v2">
						<p class="leading-[20px]">
							<Show
								when={nextPaymentDate()}
								fallback="Changes will apply at the end of your billing period"
							>
								{(date) => (
									<>
										Changes will apply in {calculateDaysUntil(date())} days,{" "}
										{formatDate(date())}
									</>
								)}
							</Show>
						</p>
						<p class="leading-[20px]">
							Removed members can still use product until that day
						</p>
					</div>
				</Show>
			</Suspense>
		</div>
	);
};

interface PricingInfoCardProps {
	currentPricePerSeat: number | null;
	currentSeats: number;
	newPricePerSeat: number | null;
	newSeats: number | null;
	totalSum: number | null;
	subscriptionType?: "monthly" | "annually";
}

const PricingInfoCard: Component<PricingInfoCardProps> = (props) => {
	return (
		<>
			{/* Current plan */}
			<div class="typography-text-14-regular flex w-full items-center justify-between whitespace-pre-wrap">
				<span class="typography-text-14-semibold flex-1 text-text-secondary-v2">
					Current plan:
				</span>
				<span class="typography-text-14-medium flex-1 text-right text-text-secondary">
					<Show when={props.currentPricePerSeat !== null} fallback="–">
						${props.currentPricePerSeat} per seat/month ({props.currentSeats} seats)
					</Show>
				</span>
			</div>

			{/* New plan */}
			<div class="typography-text-14-regular flex w-full items-center justify-between whitespace-pre-wrap">
				<span class="typography-text-14-semibold flex-1 text-text-secondary-v2">
					New plan:
				</span>
				<span
					class={twJoin(
						"typography-text-14-medium flex-1 text-right",
						props.newPricePerSeat !== null && props.newSeats !== null
							? "text-status-accent-error"
							: "text-text-secondary"
					)}
				>
					<Show
						when={props.newPricePerSeat !== null && props.newSeats !== null}
						fallback="–"
					>
						${props.newPricePerSeat} per seat/month ({props.newSeats} seats)
					</Show>
				</span>
			</div>

			{/* Sum */}
			<div class="flex w-full items-center justify-between whitespace-pre-wrap">
				<span class="typography-text-14-semibold text-text-secondary-v2">Sum:</span>
				<span class="typography-text-14-medium text-text-secondary-v2">
					<Show when={props.totalSum !== null} fallback="–">
						${props.totalSum}/{props.subscriptionType === "annually" ? "year" : "month"}
					</Show>
				</span>
			</div>
		</>
	);
};

const PricingInfo: Component = () => {
	const { calculationQuery, teamQuery, selectedCount } = useUpdateSeatsContext();

	// Memoize expensive calculations
	const pricingData = createMemo(() => {
		const teamData = teamQuery.data;
		const calcData = calculationQuery.data;

		// Current data
		const currentSeats = teamData?.team_member_total_amount || 0;

		// New data (if seats are selected)
		const hasSelection = selectedCount() > 0;

		// Calculate current price per seat only when seats are selected
		let currentPricePerSeat: number | null = null;
		if (hasSelection) {
			if (calcData?.current_monthly_cost_per_seat) {
				// Use API value if available
				currentPricePerSeat = calcData.current_monthly_cost_per_seat;
			} else if (teamData?.next_payment?.total_amount && currentSeats > 0) {
				// Fallback: calculate from next_payment total amount
				currentPricePerSeat = teamData.next_payment.total_amount / currentSeats;
			}
		}

		const newSeats = hasSelection ? currentSeats - selectedCount() : null;
		const newPricePerSeat =
			hasSelection && calcData?.new_monthly_cost_per_seat !== undefined
				? calcData.new_monthly_cost_per_seat
				: null;
		// Show totalSum only when there's a selection and total_cost is different from current
		const totalSum =
			hasSelection &&
			calcData?.total_cost !== undefined &&
			calcData?.total_cost !== calcData?.current_monthly_cost
				? calcData.total_cost
				: null;
		const subscriptionType = calcData?.subscription_type;

		return {
			currentPricePerSeat,
			currentSeats,
			newPricePerSeat,
			newSeats,
			totalSum,
			subscriptionType
		};
	});

	return (
		<Suspense fallback={<PricingInfoCard {...untrack(pricingData)} />}>
			<PricingInfoCard {...pricingData()} />
		</Suspense>
	);
};

const PricingFooter: Component = () => {
	const { isCalculating } = useUpdateSeatsContext();
	return (
		<div class="rounded-[20px]] relative overflow-hidden">
			<Show when={isCalculating()}>
				<div class="absolute h-full w-full bg-white opacity-80" />
				<div class="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2">
					<Spinner />
				</div>
			</Show>
			<div class={"flex w-full flex-col gap-3.5 rounded-[20px] bg-gray-100 p-5"}>
				<InfoBanner />
				<PricingInfo />
			</div>
		</div>
	);
};

export const UpdateSeatsStepContent: Component = () => {
	return (
		<ModalV2Ui.ContentContainer>
			<div class="flex w-full flex-col gap-8">
				<div class="flex w-full flex-col gap-3">
					<MemberListInfo />
					<MembersList />
				</div>
				<PricingFooter />
			</div>
		</ModalV2Ui.ContentContainer>
	);
};
